import React, { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { IoMdArrowDropdown } from 'react-icons/io';
import TagSelecionada from './TagSelecionada';
import { fetchData } from '../../services/fetch';
import { useGriSeries } from '../../hooks/useGriSeries';

/**
 * GRI-02 — CA01: seleção de indicadores GRI da questão.
 *
 * Popover e não Menu, pelo mesmo motivo do FilterCombobox: o Menu do Chakra tem
 * typeahead e rouba o foco do Input a cada tecla.
 *
 * SEM Portal: este campo aparece dentro do modal de edição da questão e do
 * modal de classificação em massa, e o Modal do Chakra prende o foco no próprio
 * conteúdo — painel portalado para fora fica fora da trava e não dá para
 * clicar. `strategy='fixed'` sozinho resolve o que o Portal resolvia aqui, que
 * era escapar do contêiner com rolagem.
 *
 * Diferente do FilterCombobox em duas coisas que o multi exige: o painel NÃO
 * fecha ao escolher (escolher vários é o caso normal, CA1.5) e o gatilho mostra
 * o que já está escolhido em vez de um texto só.
 */
const GriMultiSelect = ({
  value = [],
  onChange,
  isDisabled = false,
  placeholder = 'Selecione os indicadores',
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [busca, setBusca] = useState('');
  const [serie, setSerie] = useState('');
  const buscaRef = useRef(null);

  const { data: indicadores, isLoading } = useSWR('/gri-indicator', fetchData);
  const { options: seriesOptions, labels: seriesLabels } = useGriSeries();

  const selecionados = useMemo(
    () => (value || []).map(Number).filter((id) => !Number.isNaN(id)),
    [value]
  );

  const catalogo = useMemo(() => indicadores || [], [indicadores]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return catalogo.filter((item) => {
      if (serie && item.series !== serie) return false;
      if (!termo) return true;

      // CA1.3: busca por código E por nome. Quem lembra "305-1" e quem lembra
      // "emissões diretas" precisam chegar no mesmo item.
      return (
        item.code?.toLowerCase().includes(termo) ||
        item.name?.toLowerCase().includes(termo)
      );
    });
  }, [catalogo, busca, serie]);

  // Indicador já vinculado que saiu do catálogo ativo (exclusão lógica do
  // GRI-01) continua aparecendo como tag: o vínculo histórico é preservado, e
  // sumir com ele daria a impressão de que a questão perdeu a classificação.
  const escolhidos = useMemo(
    () =>
      selecionados.map(
        (id) =>
          catalogo.find((item) => item.id_gri_indicator === id) || {
            id_gri_indicator: id,
            code: `#${id}`,
          }
      ),
    [selecionados, catalogo]
  );

  const alternar = (id) => {
    const proximo = selecionados.includes(id)
      ? selecionados.filter((atual) => atual !== id)
      : [...selecionados, id];

    onChange(proximo);
  };

  const fechar = () => {
    setBusca('');
    onClose();
  };

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={fechar}
        initialFocusRef={buscaRef}
        placement='bottom-start'
        matchWidth
        isLazy
        strategy='fixed'
      >
        <PopoverTrigger>
          <Button
            w='100%'
            h='40px'
            px='12px'
            bg='white'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='8px'
            fontSize='sm'
            fontWeight={500}
            color={escolhidos.length ? 'black' : 'gray.500'}
            _hover={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            isLoading={isLoading}
            isDisabled={isDisabled}
            rightIcon={<IoMdArrowDropdown fontSize='22px' opacity={0.5} />}
          >
            <Text flex='1' textAlign='left' noOfLines={1}>
              {escolhidos.length
                ? `${escolhidos.length} selecionado${
                    escolhidos.length > 1 ? 's' : ''
                  }`
                : placeholder}
            </Text>
          </Button>
        </PopoverTrigger>

        <PopoverContent borderRadius='8px' _focusVisible={{ outline: 'none' }}>
          <PopoverBody p='8px'>
              <Input
                ref={buscaRef}
                size='sm'
                borderRadius='6px'
                placeholder='Buscar por código ou nome'
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                mb='8px'
              />

              {/* CA1.4: filtro por série. Com 60 indicadores a busca por texto
                  resolve o caso de quem sabe o que quer; o filtro serve a quem
                  está varrendo uma dimensão inteira. */}
              <Flex gap='4px' mb='8px' wrap='wrap'>
                <FiltroDeSerie
                  ativo={!serie}
                  onClick={() => setSerie('')}
                  rotulo='Todas'
                />
                {seriesOptions.map((opcao) => (
                  <FiltroDeSerie
                    key={opcao.value}
                    ativo={serie === opcao.value}
                    onClick={() => setSerie(opcao.value)}
                    rotulo={opcao.label}
                  />
                ))}
              </Flex>

              <Divider mb='6px' />

              <Box maxH='240px' overflowY='auto'>
                {filtrados.map((item) => (
                  <Box
                    key={item.id_gri_indicator}
                    px='8px'
                    py='6px'
                    borderRadius='6px'
                    _hover={{ bg: '#F4F6F8' }}
                  >
                    <Checkbox
                      isChecked={selecionados.includes(item.id_gri_indicator)}
                      onChange={() => alternar(item.id_gri_indicator)}
                      alignItems='flex-start'
                    >
                      {/* CA1.2: código e nome juntos — o código sozinho não diz
                          o que a questão reporta, e o nome sozinho não casa com
                          o que o relatório GRI cobra. */}
                      <Text fontSize='sm' lineHeight='1.3'>
                        <Text as='span' fontWeight={600}>
                          {item.code}
                        </Text>
                        {' — '}
                        {item.name}
                      </Text>
                      <Text fontSize='xs' color='#818A8F'>
                        {seriesLabels[item.series] || item.series}
                      </Text>
                    </Checkbox>
                  </Box>
                ))}

                {filtrados.length === 0 && (
                  <Text fontSize='sm' color='#818A8F' px='8px' py='12px'>
                    Nenhum indicador encontrado
                  </Text>
                )}
              </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {escolhidos.length > 0 && (
        <Flex gap='6px' wrap='wrap' mt='8px'>
          {escolhidos.map((item) => (
            <TagSelecionada
              key={item.id_gri_indicator}
              label={item.code}
              onRemove={() => alternar(item.id_gri_indicator)}
              isDisabled={isDisabled}
              maxW='160px'
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

const FiltroDeSerie = ({ ativo, onClick, rotulo }) => (
  <Box
    as='button'
    type='button'
    px='8px'
    py='2px'
    borderRadius='full'
    fontSize='12px'
    fontWeight={ativo ? 600 : 400}
    bg={ativo ? '#EAF2FB' : '#F4F6F8'}
    color={ativo ? '#0073BB' : '#666666'}
    onClick={onClick}
  >
    {rotulo}
  </Box>
);

export default GriMultiSelect;
